import ExpandingCard from "common/Boxes/ExpandingCard";
import Col from "antd/es/col";
import Row from "antd/es/row";
import Flex from "common/Boxes/Flex";
import IconWithFallback from "Global/IconWithFallback";
import {
  ICON_TYPES,
  SIGNAL_EXTRA_DETAILS_TABS,
} from "@shared/common/constants";
import { SignalFollowAlertIcon } from "features/signals/SignalFollow/SignalFollowAlertIcon";
import AutoSizeText from "common/Text/AutoSizeText";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import { getPairFromSymbol, hexToRGB } from "@shared/utils/misc";
import Text from "common/Typography/Text";
import React, { MouseEvent, useContext } from "react";
import {
  CardsSelectorContext,
  ContextProps,
} from "@shared/common/CardsList/CardsSelector.context";
import { PrecisionType, SignalDataWithKey } from "@shared/types";
import { typography } from "@shared/common/Themes/typography";
import styled, { useTheme } from "styled-components";
import ChangeTag from "common/Tags/ChangeTag";
import { toFixed } from "@shared/utils/numbers";
import TradeProgressGraph from "features/trades/common/TradeProgressGraph";
import SignalCardActions from "features/signals/SignalsCards/SignalCardActions";
import { ExchangesIconsGroup } from "features/portfolio/Assets/ExchangesIconsGroup";
import TextWithIcon from "common/Text/TextWithIcon";
import Tag from "common/Tags/Tag";
import { SIGNAL_CARD_COLUMN_SPANS } from "features/signals/common/constants";
import { useSignalCard } from "@shared/features/signals/common/hooks";
import TradeTimeInfo from "features/trades/TradesCards/TradeTimeInfo";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { getSignalExtraDetailsRoute } from "utils/routes.tsx";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { useDisplayTradeDetails } from "@shared/utils/hooks/misc.ts";
import Checkbox from "common/Checkbox/Checkbox.tsx";

interface SignalCardProps {
  signalData: SignalDataWithKey;
  isExpanded?: boolean;
  toggleExpanded: (key: string | number) => void;
}

const SignalCard = ({
  signalData,
  isExpanded,
  toggleExpanded,
}: SignalCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const theme = useTheme();
  const { historyPush } = useHistoryWithQuery();
  const { precision: pricePrecision } = usePrecisions({
    precisionType: PrecisionType.PRICE,
    symbol: signalData.symbol,
    exchange: signalData.exchange,
  });
  const { getExtraInfoValues } = useSignalCard();
  const displayTradeDetails = useDisplayTradeDetails();

  const { getCheckboxProps, isEditMode } =
    useContext<ContextProps<SignalDataWithKey> | undefined>(
      CardsSelectorContext
    ) || {};
  const checkboxProps = getCheckboxProps?.(signalData);

  const onLongPress = () => {
    historyPush(
      getSignalExtraDetailsRoute(
        location.pathname,
        signalData.signal_id,
        SIGNAL_EXTRA_DETAILS_TABS.GENERAL,
        false
      )
    );
  };

  const getDisplayedExchanges = (exchanges: string[]) => {
    if (exchanges.length > 1) {
      return (
        <div onClick={(e) => e.stopPropagation()}>
          <ExchangesIconsGroup
            exchanges={exchanges}
            size={24}
            style={{ left: 8, position: "relative" }}
          />
        </div>
      );
    } else {
      return (
        <TextWithIcon
          iconType={ICON_TYPES.EXCHANGE}
          coinOrExchangeName={exchanges[0]}
          text={exchanges[0]}
        />
      );
    }
  };

  const extraInfo = getExtraInfoValues(signalData);
  const collapsibleContent = (
    <div style={{ padding: 4 }}>
      <Row gutter={[12, 16]}>
        <Col span={12}>{getDisplayedExchanges(extraInfo.exchanges)}</Col>
        <Col span={12} style={{ display: "flex" }}>
          <Tag
            color={theme.colors.primaryGrey}
            bgColor={hexToRGB(theme.colors.secondaryGrey, 0.1)}
            style={{ maxWidth: "100%" }}
          >
            <Text $ellipsis={true} style={{ fontSize: 11 }}>
              {extraInfo.group}
            </Text>
          </Tag>
        </Col>
        <Col span={12}>
          <TradeTimeInfo
            timestamp={extraInfo.timestamp}
            isOpen={signalData.is_open}
          />
        </Col>
        <Col span={12}>
          <Text>
            {extraInfo.longShort}
            {extraInfo.margin}
          </Text>
        </Col>
      </Row>
    </div>
  );

  const subTextStyle = {
    fontSize: typography.pxFontSizes.S,
    color: theme.colors.midDark,
  };

  const symbol = (
    <SymbolWrapper>
      <div style={{ position: "relative" }}>
        <IconWithFallback
          coinOrExchangeName={signalData.symbol}
          iconType={ICON_TYPES.COIN}
          pxIconSize={30}
          iconStyle={{ marginRight: 14 }}
        />
        <FollowIconWrapper>
          <SignalFollowAlertIcon signal={signalData} />
        </FollowIconWrapper>
      </div>
      <div style={{ width: "100%", marginRight: 8 }}>
        <AutoSizeText>{signalData.symbol}</AutoSizeText>
        {signalData.is_open &&
          (signalData.price ? (
            <TextWithPrecisionAndIcon
              text={signalData.price}
              iconType={ICON_TYPES.COIN}
              coinOrExchangeName={getPairFromSymbol(signalData.symbol)}
              precision={pricePrecision}
              textStyle={subTextStyle}
              style={{ marginTop: 7 }}
              iconStyle={{ fontSize: typography.fontSizes.S, marginRight: 4 }}
            />
          ) : (
            <Text>{t("table.na")}</Text>
          ))}
      </div>
    </SymbolWrapper>
  );

  const ratio = toFixed(
    (signalData.signal_extra_data?.["Potential Profit Ratio"] || 0) * 100,
    2
  );
  const potentialProfit = (
    <ProfitWrapper>
      <ChangeTag
        arrowStyle={{ fontSize: 8 }}
        value={ratio}
        suffix="%"
        style={{ marginLeft: 0 }}
        hideBackground={true}
      />
      <Text style={{ ...subTextStyle, marginTop: 7 }}>
        {signalData.signal_extra_data?.["Risk/Reward"] || t("table.na")}
      </Text>
    </ProfitWrapper>
  );

  const progress = (
    <TradeProgressGraph
      graphData={signalData.graph_data}
      size={"s"}
      style={GRAPH_STYLE}
    />
  );

  return (
    <ExpandingCard
      collapsibleContent={collapsibleContent}
      expanded={isExpanded}
      setExpanded={() => toggleExpanded?.(signalData.key)}
      style={{ padding: "8px 12px" }}
      onLongPress={displayTradeDetails ? onLongPress : undefined}
    >
      <Row style={{ alignItems: "center" }}>
        <Col span={SIGNAL_CARD_COLUMN_SPANS[0]}>{symbol}</Col>
        <Col span={SIGNAL_CARD_COLUMN_SPANS[1]}>{potentialProfit}</Col>
        <Col span={SIGNAL_CARD_COLUMN_SPANS[2]}>{progress}</Col>
        <ActionsWrapper
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            if (isEditMode && checkboxProps) {
              checkboxProps.onClick();
            }
          }}
          style={{ right: isEditMode ? "-4px" : "-9px" }}
        >
          {isEditMode && checkboxProps ? (
            <Checkbox checked={checkboxProps.isSelected} $size={18} />
          ) : (
            <SignalCardActions signal={signalData} />
          )}
        </ActionsWrapper>
      </Row>
    </ExpandingCard>
  );
};

export default React.memo(SignalCard);

const GRAPH_STYLE = { margin: 0 };

const FollowIconWrapper = styled.div`
  position: absolute;
  bottom: -8px;
  right: 8px;
`;
const SymbolWrapper = styled(Flex)`
  justify-content: flex-start;
  width: 100%;
`;
const ProfitWrapper = styled(Flex)`
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
`;
const ActionsWrapper = styled.div`
  position: absolute;
`;
